import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, LifeBuoy, Mail, Phone, MessageSquare, ShieldAlert } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

/**
 * Internal Help Desk — replacement for "Contact Us" on NECS employee
 * accounts. Surfaces the HR / IT escalation paths that aren't
 * appropriate to show customer-facing in the public Contact form.
 */
export default function HelpDeskPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <p className="text-zinc-600 text-sm">Please sign in to access the internal Help Desk.</p>
      </div>
    );
  }

  // Customer-facing accounts should not be here.
  if (['property_manager', 'tenant', 'tenant_resident'].includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Card className="p-6 max-w-md text-center">
          <p className="text-zinc-700 text-sm mb-4">This page is for NECS employees. Please use Contact Us instead.</p>
          <Button onClick={() => navigate('/contact')}>Go to Contact Us</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="help-desk-page">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/profile" className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-2 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to Profile
        </Link>

        <Card className="bg-white p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#C9A962' }}>
              <LifeBuoy className="h-6 w-6 text-black" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.04em' }}>
                INTERNAL HELP DESK
              </h1>
              <p className="text-sm text-zinc-500">NECS-only — for HR, IT, and operational escalations.</p>
            </div>
          </div>

          <div className="space-y-3">
            <Channel icon={Phone} label="HR direct line" value="+1 (888) 241-3077" testid="help-hr-phone" />
            <Channel icon={Mail} label="HR email" value="hr@nobleempirecs.com" testid="help-hr-email" />
            <Channel icon={ShieldAlert} label="IT / Security incidents" value="it@nobleempirecs.com" testid="help-it-email" />
            <Channel
              icon={MessageSquare}
              label="Operations support chat"
              value="Open the AI Operations assistant from the dashboard sidebar."
              testid="help-ops-chat"
            />
          </div>

          <div className="mt-8 p-4 rounded-lg bg-zinc-50 border border-zinc-200">
            <p className="text-xs text-zinc-600 leading-relaxed">
              For attendance-related concerns (point disputes, FMLA requests, schedule conflicts),
              please use the Attendance tab in your dashboard to file a documented request
              before contacting HR — that way your manager sees the same record HR will reference.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Channel({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border border-zinc-200" data-testid={testid}>
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4 text-zinc-500" />
        <span className="text-sm font-semibold text-zinc-700">{label}</span>
      </div>
      <span className="text-sm text-zinc-900 font-mono">{value}</span>
    </div>
  );
}
